import React, { useEffect, useState } from 'react';
import styles from './Table.module.scss';
import { useTranslation } from 'react-i18next';
import NumberFormat from 'react-number-format';
import { Pagination } from '@material-ui/lab';
import '../../Util/Pagination/Pagination.scss';
import usePaginationSort from '../../../hooks/usePaginationSort';
import useTable from '../../../hooks/useTable';
import { commonHeaderCurrent, commonHeaderHiddenCurrent } from '../PortfolioDetailsUtil';
import { ExcelDownloadButton } from '../../Util/ExcelDownloadButton/ExcelDownloadButton';
import useDataNormalize from '../../../hooks/useDataNormalize';
import { Tooltip } from '@material-ui/core';
import PaginationTable from '../PaginationTable/PaginationTable';
import { withStyles } from '@material-ui/core/styles';
import { FilterTables } from '../../Filter/FilterTables/FilterTables';
import { SelectFilterData } from './SelectFilterData';
import { ProjectsListSkeleton } from '../../LoadersSkeletons/ProjectsListSkeleton/ProjectsListSkeleton';
import SearchTabErrorMessage from '../../Util/Errors/SearchTabErrorMessage/SearchTabErrorMessage';

const BlackTooltip = withStyles({
  tooltip: {
    //   color: '$ci-color-greyscale-90',
    backgroundColor: '#101319',
    fontSize: '16px',
    fontFamily: 'Rubik',
    fontWeight: 400,
    lineHeight: '20px',
    padding: '8px 12px',
    borderRadius: '8px',
  },
  arrow: {
    fontSize: 20,
    color: '#4A4A4A',
    '&::before': {
      backgroundColor: '#101319',
    },
  },
})(Tooltip);

const customRowStyle = project => (project.id === 0 ? { cursor: 'default' } : {});

type Props = {
  handleClickBarSelect: (string) => void;
  valueFilterTextDefault: string;
  loadingTable: boolean;
  totalValuesHeader: {
    disburment: number;
    INV: number;
    PBL: number;
  };
};

const Table = ({ handleClickBarSelect, valueFilterTextDefault, loadingTable, totalValuesHeader }: Props) => {
  const { t } = useTranslation();
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { handleClick, projects } = useTable();
  const [dataFilter, setDataFilter] = useState<any>(projects);
  const [searchTerm, setSearchTerm] = useState('');
  const date = new Date();
  const year = date.getFullYear();
  const hasActiveSearch = searchTerm.trim().length >= 3;

  const headers =
    valueFilterTextDefault === year.toString()
      ? [
        'year',
        'country',
        'last_project',
        'last_operation',
        'name',
        'division',
        'original_projection',
        'current_projection',
        'amount_disbursed',
        'instrument',
        'current_stage',
      ]
      : [
        'year',
        'country',
        'last_project',
        'last_operation',
        'name',
        'division',
        'original_projection',
        'amount_disbursed',
        'instrument',
        'current_stage',
      ];

  const selectedKeys =
    valueFilterTextDefault === year.toString()
      ? ['field1', 'field2', 'code', 'loanNumber', 'title', 'field3', 'field4', 'field8', 'field5', 'field6', 'field7']
      : ['field1', 'field2', 'code', 'loanNumber', 'title', 'field3', 'field4', 'field5', 'field6', 'field7'];

  useEffect(() => {
    setDataFilter(projects);
  }, [projects]);

  const { handlePaginationChange, handleSort, currentList, pagesCount, currentPage } = usePaginationSort({
    projects: dataFilter,
    defaultSortCriteria: 'field2',
    pageCount: rowsPerPage,
  });

  const {
    container,
    tableHeader,
    tableHeaderHidden,
    tableRow,
    tableRowHidden,
    headerTitle,
    rowValue,
    rowList,
    rowValueName,
    fw,
    lastColumn,
    headerTitleClickable,
    headerTitleClickableHidden,
    rowValueClosed,
    borderTable,
  } = styles;

  const tableHeaderColumns =
    valueFilterTextDefault === year.toString()
      ? [
        ...commonHeaderCurrent({
          headerTitleClickable,
          t,
          handleSort,
          field3Translation: t('division'),
          field5Translation: `${t('amount_disbursed')}`,
        }),
      ]
      : [
        ...commonHeaderHiddenCurrent({
          headerTitleClickableHidden,
          t,
          handleSort,
          field3Translation: t('division'),
          field5Translation: `${t('amount_disbursed')}`,
        }),
      ];

  const data = useDataNormalize(headers, selectedKeys, dataFilter);

  return projects.length ? (
    <div className={container}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <div className={`${styles['filter-style']}`}>
          <SelectFilterData
            handleClickBarSelect={handleClickBarSelect}
            valueFilterTextDefault={valueFilterTextDefault}
          />
        </div>
        <div className={`${styles['container-line']}`}>
          <div className={`${styles['division-style']}`}></div>
        </div>
        <div className={`${styles['amount-disbursed']}`}>
          <p>{t('new_total_disbursed')}</p>
          <p>
            <b>
              <NumberFormat
                value={totalValuesHeader?.disburment}
                displayType={'text'}
                thousandSeparator={'.'}
                decimalSeparator={','}
                decimalScale={1}
              />{' '}
              <span>USD</span>
            </b>
          </p>
        </div>
        <div className={`${styles['amount-disbursed-instrument']}`}>
          <p>{t('new_amount_disbursed_in_INV')}</p>
          <p>
            <b>
              <NumberFormat
                value={totalValuesHeader?.INV}
                displayType={'text'}
                thousandSeparator={'.'}
                decimalSeparator={','}
                decimalScale={1}
              />{' '}
              <span>USD</span>
            </b>
          </p>
        </div>
        <div className={`${styles['amount-disbursed-instrument']}`}>
          <p>{t('new_amount_disbursed_in_PBL')}</p>
          <p>
            <b>
              <NumberFormat
                value={totalValuesHeader?.PBL}
                displayType={'text'}
                thousandSeparator={'.'}
                decimalSeparator={','}
                decimalScale={1}
              />{' '}
              <span>USD</span>
            </b>
          </p>
        </div>

        <ExcelDownloadButton headers={headers} selectedKeys={selectedKeys} dataExcel={data} />
        <FilterTables
          projects={projects}
          keysFilter={selectedKeys}
          fnDataFilter={setDataFilter}
          disabledNumber={currentPage}
          indicatorKey={'DISBURSEMENT_PROJECTION_LAST_5_YEARS'}
          onSearchChange={setSearchTerm}
        />
      </div>
      {!loadingTable ? (
        <div className={borderTable}>
          <div>
            {dataFilter?.length === 0 && hasActiveSearch ? (
              <div className='tableHeader'></div>
            ) : (
              <div className={valueFilterTextDefault === year.toString() ? tableHeader : tableHeaderHidden}>
                <h4 className={headerTitle}>{t('year')}</h4>
                {tableHeaderColumns.map(column => {
                  return (
                    <h4 className={column.classNameStyle} onClick={column.sortFunction}>
                      {column.translation}
                    </h4>
                  );
                })}
              </div>
            )}
          </div>
          {dataFilter?.length === 0 && hasActiveSearch ? (
            <div className='m-4'>
              <SearchTabErrorMessage search={searchTerm} />
            </div>
          ) : (
            <>
              <ul className={rowList}>
                {dataFilter?.length === 0
                  ? dataFilter?.map((project, index) => (
                    <li
                      className={valueFilterTextDefault === year.toString() ? tableRow : tableRowHidden}
                      key={`${project.loanNumber}${project.code}${project.frontendId}`}
                      style={customRowStyle(project)}
                    >
                      <p className={rowValue}>{project.field1}</p>

                      <p className={rowValue}>{project.field2}</p>
                      {project.id ? (
                        <BlackTooltip title={t('go_to_project_info')} arrow>
                          <p className={`${rowValue} link-project`} onClick={() => handleClick(project)}>
                            {project.code}{' '}
                          </p>
                        </BlackTooltip>
                      ) : (
                        <p className={`${rowValue}`}>{project.code}</p>
                      )}

                      <p className={rowValue}>{project.loanNumber}</p>
                      {project.title ? (
                        <BlackTooltip
                          title={project.title || ''}
                          placement="bottom"
                          enterDelay={500}
                          leaveDelay={100}
                          arrow
                        >
                          <p className={rowValueName}>{project.title}</p>
                        </BlackTooltip>
                      ) : (
                        <p className={rowValueName}>{'-'}</p>
                      )}

                      <p className={rowValue}>{project.field3}</p>
                      <p className={rowValue}>
                        {project.field4 ? (
                          <NumberFormat
                            value={parseInt(project.field4)}
                            displayType={'text'}
                            className={'value'}
                            thousandSeparator={'.'}
                            decimalSeparator={','}
                          />
                        ) : (
                          '-'
                        )}
                      </p>
                      {valueFilterTextDefault === year.toString() && (
                        <p className={rowValue}>
                          {project.field8 ? (
                            <NumberFormat
                              value={parseInt(project.field8)}
                              displayType={'text'}
                              className={'value'}
                              thousandSeparator={'.'}
                              decimalSeparator={','}
                            />
                          ) : (
                            '-'
                          )}
                        </p>
                      )}
                      <p className={rowValue}>
                        {project.field5 ? (
                          <NumberFormat
                            value={parseInt(project.field5)}
                            displayType={'text'}
                            className={'value'}
                            thousandSeparator={'.'}
                            decimalSeparator={','}
                          />
                        ) : (
                          '-'
                        )}
                      </p>
                      <p className={rowValue}>{project.field6}</p>
                      <p className={rowValue}>{project.field7}</p>
                    </li>
                  ))
                  : currentList?.map((project, index) => (
                    <li
                      className={valueFilterTextDefault === year.toString() ? tableRow : tableRowHidden}
                      key={`${project.loanNumber}${project.code}${project.frontendId}`}
                      style={customRowStyle(project)}
                    >
                      <p className={rowValue}>{project.field1}</p>

                      <p className={rowValue}>{project.field2}</p>
                      {project.id ? (
                        <BlackTooltip title={t('go_to_project_info')} arrow>
                          <p className={`${rowValue} link-project`} onClick={() => handleClick(project)}>
                            {project.code}{' '}
                          </p>
                        </BlackTooltip>
                      ) : (
                        <p className={`${rowValue}`}>{project.code}</p>
                      )}

                      <p className={rowValue}>{project.loanNumber}</p>
                      {project.title ? (
                        <BlackTooltip
                          title={project.title || ''}
                          placement="bottom"
                          enterDelay={500}
                          leaveDelay={100}
                          arrow
                        >
                          <p className={rowValueName}>{project.title}</p>
                        </BlackTooltip>
                      ) : (
                        <p className={rowValueName}>{'-'}</p>
                      )}

                      <p className={rowValue}>{project.field3}</p>
                      <p className={rowValue}>
                        {project.field4 ? (
                          <NumberFormat
                            value={parseInt(project.field4)}
                            displayType={'text'}
                            className={'value'}
                            thousandSeparator={'.'}
                            decimalSeparator={','}
                          />
                        ) : (
                          '-'
                        )}
                      </p>
                      {valueFilterTextDefault === year.toString() && (
                        <p className={rowValue}>
                          {project.field8 ? (
                            <NumberFormat
                              value={parseInt(project.field8)}
                              displayType={'text'}
                              className={'value'}
                              thousandSeparator={'.'}
                              decimalSeparator={','}
                            />
                          ) : (
                            '-'
                          )}
                        </p>
                      )}
                      <p className={rowValue}>
                        {project.field5 ? (
                          <NumberFormat
                            value={parseInt(project.field5)}
                            displayType={'text'}
                            className={'value'}
                            thousandSeparator={'.'}
                            decimalSeparator={','}
                          />
                        ) : (
                          '-'
                        )}
                      </p>
                      <p className={rowValue}>{project.field6}</p>
                      <p className={rowValue}>{project.field7}</p>
                    </li>
                  ))}
              </ul>

              <div className="pagination-style">
                <PaginationTable fnSetRows={setRowsPerPage} rows={rowsPerPage} numProyects={dataFilter?.length} />
                {dataFilter?.length !== 0 ? (
                  <Pagination count={pagesCount} page={currentPage} onChange={handlePaginationChange} />
                ) : null}
              </div>
            </>
          )}
        </div>
      ) : (
        <ProjectsListSkeleton />
      )}
    </div>
  ) : null;
};

export default Table;
